<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import {
    arrivalTimeIssues,
    nextArrivalTime,
  } from '@/domain/reservations/reservation-preview';
  import type { TArrivalTimeIssue } from '@/domain/reservations/reservation-preview';
  import type { IReservationArrivalTime } from '@/interfaces/IReservationSettings';

  const MAX_ROWS = 96;
  const props = defineProps<{
    value: IReservationArrivalTime[];
    from: string;
    to: string;
    intervalMin: number;
    disabled?: boolean;
    /** Show row errors (after a submit attempt). */
    showErrors?: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'input', value: IReservationArrivalTime[]): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const issues = computed(() =>
    arrivalTimeIssues(props.from, props.to, props.value)
  );
  const issueKeys: Record<TArrivalTimeIssue, string> = {
    invalid: 'reservations.shifts.form.arrivalInvalid',
    outside: 'reservations.shifts.form.arrivalOutside',
    tail: 'reservations.shifts.form.arrivalTail',
    duplicate: 'reservations.shifts.form.arrivalDuplicate',
    cap: 'reservations.shifts.form.arrivalCapInvalid',
  };
  function issueText(index: number): string {
    const issue = issues.value[index];
    return issue ? String(proxy.$t(issueKeys[issue])) : '';
  }
  function patch(index: number, changes: Partial<IReservationArrivalTime>) {
    emit(
      'input',
      props.value.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );
  }
  function setTime(index: number, event: Event) {
    if (event.target instanceof HTMLInputElement)
      patch(index, { time: event.target.value });
  }
  function setCap(index: number, value: string | number | null) {
    const text = String(value ?? '').trim();
    patch(index, { maxCovers: text === '' ? null : Number(text) });
  }
  // Rows keep the order they were entered in (the list is sorted on save), so
  // no row moves under a focused input.
  function add() {
    if (props.value.length >= MAX_ROWS) return;
    emit('input', [
      ...props.value,
      {
        time: nextArrivalTime(props.from, props.value, props.intervalMin),
        maxCovers: null,
      },
    ]);
  }
  function remove(index: number) {
    emit(
      'input',
      props.value.filter((_, i) => i !== index)
    );
  }
</script>

<template>
  <div class="arrival-times-editor">
    <div class="rows-header">
      <span>{{ $t('reservations.shifts.form.arrivalTime') }}</span>
      <span>{{ $t('reservations.shifts.form.arrivalCap') }}</span>
    </div>
    <div
      v-for="(row, index) in value"
      :key="index"
      class="arrival-row"
      :class="{ invalid: showErrors && issues[index] }"
    >
      <input
        :id="`rsv-arrival-time-${index}`"
        class="native-input"
        type="time"
        :value="row.time"
        :disabled="disabled"
        :aria-label="$t('reservations.shifts.form.arrivalTime')"
        :aria-invalid="showErrors && !!issues[index]"
        @input="setTime(index, $event)"
      />
      <label class="cap-label" :for="`rsv-arrival-cap-${index}`">
        <span class="sr-only">{{
          $t('reservations.shifts.form.arrivalCap')
        }}</span>
        <PosUiInput
          :id="`rsv-arrival-cap-${index}`"
          type="number"
          inputmode="numeric"
          clearable
          :value="row.maxCovers"
          :placeholder="$t('reservations.settings.pacingUnlimited')"
          :disabled="disabled"
          @input="setCap(index, $event)"
        />
      </label>
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="close"
        :aria-label="`${$t('delete')} ${row.time}`"
        :disabled="disabled"
        @click="remove(index)"
      />
      <p v-if="showErrors && issues[index]" class="row-error" role="alert">
        {{ issueText(index) }}
      </p>
    </div>
    <p v-if="showErrors && !value.length" class="row-error" role="alert">
      {{ $t('reservations.shifts.form.arrivalsRequired') }}
    </p>
    <PosUiButton
      color="neutral"
      variant="outline"
      icon="plus"
      :disabled="disabled || value.length >= MAX_ROWS"
      @click="add"
      >{{ $t('reservations.shifts.form.addArrival') }}</PosUiButton
    >
  </div>
</template>

<style scoped lang="scss">
  .arrival-times-editor {
    @apply tw-flex tw-flex-col tw-gap-2;
    .rows-header,
    .arrival-row {
      @apply tw-grid tw-items-center tw-gap-2;
      grid-template-columns: minmax(0, 1fr) minmax(0, 7.5rem) 3.25rem;
    }
    .rows-header {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .arrival-row {
      @apply tw-rounded-lg tw-bg-canvas tw-p-2;
      &.invalid {
        @apply tw-bg-red-50;
      }
    }
    .cap-label {
      @apply tw-m-0 tw-min-w-0;
    }
    .sr-only {
      @apply tw-sr-only;
    }
    .native-input {
      @apply tw-min-h-11 tw-min-w-0 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
    }
    .row-error {
      @apply tw-col-span-3 tw-m-0 tw-text-xs tw-text-red-600;
    }
  }
</style>
